import { SupabaseClient } from '@supabase/supabase-js';
import {
  ArticleProfile,
  ClassProfile,
  HomeworkV2Profile,
  HomeworkStudentProfile,
  StudentProfile,
  StudentWithAnalytics,
} from '@grcs/types/DataTypes';
import { parseQuestions } from '@grcs/utils/content/parseQuestions';
import { TABLE_NAMES } from '@grcs/utils/database/types';

interface ArticleAnalyticsRow extends ArticleProfile {
  total_time: number;
  average_time: number;
  completed: number;
  total_students: number;
  percent_completed: number;
  total_homeworks: number;
  average_score: number;
  homework_tokens: string[];
  relation_count: number;
  timeVariability: number;
  avgSessionDuration: number;
  timeValues: number[];
  sessionDurations: number[];
}

export interface StudentAnalyticsRow extends StudentWithAnalytics {
  average_score: number;
}

export interface EnhancedHomeworkStudentAnalytics
  extends HomeworkStudentProfile {
  performance: 'excellent' | 'good' | 'average' | 'poor';
  timeCategory: 'fast' | 'average' | 'slow';
  completionPercentage: number;
}

export interface HomeworkQuestionAnalytics {
  questionNumber: number;
  correctAnswers: number;
  totalAttempts: number;
  difficulty: 'easy' | 'medium' | 'hard';
}

export interface HomeworkAnalyticsData {
  students: EnhancedHomeworkStudentAnalytics[];
  completionRate: number;
  averageTime: number;
  averageScore: number;
  difficultyDistribution: { easy: number; medium: number; hard: number };
  timeDistribution: { fast: number; average: number; slow: number };
  questionAnalytics: HomeworkQuestionAnalytics[];
}

export interface HomeworkAnalyticsResult {
  analytics: HomeworkAnalyticsData | null;
  availableHomework: any[];
}

export interface StudentAdvancedAnalyticsDataset {
  students: StudentProfile[];
  classes: ClassProfile[];
  articles: ArticleProfile[];
  homework: HomeworkV2Profile[];
  homeworkRelations: HomeworkStudentProfile[];
}

interface StudentAnalyticsOptions {
  scopeByStudentClass?: boolean;
}

const parseQuestionAttempted = (input: unknown): Record<string, unknown> => {
  if (!input) return {};
  if (typeof input === 'object') return input as Record<string, unknown>;
  if (typeof input !== 'string') return {};

  try {
    const parsed = JSON.parse(input);
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
};

const hasAttemptedAnswers = (input: unknown) => {
  const attempted = parseQuestionAttempted(input);
  return Object.keys(attempted).length > 0;
};

const calculateAttemptScore = (input: unknown) => {
  const attempted = parseQuestionAttempted(input);
  const answers = Object.values(attempted);
  if (answers.length === 0) return null;

  const correct = answers.filter(
    (answer) => answer === true || (answer as number) > 0,
  ).length;
  return (correct / answers.length) * 100;
};

const formatQuestionAttempted = (input: unknown): Record<string, number> => {
  const parsed = parseQuestionAttempted(input);
  const normalized: Record<string, number> = {};

  Object.entries(parsed).forEach(([key, value]) => {
    normalized[key] = value === true ? 1 : value === false ? 0 : Number(value);
  });

  return normalized;
};

const calculateTimeVariability = (timeValues: number[], totalTime: number) => {
  if (timeValues.length <= 1 || totalTime <= 0) return 0;

  const average = totalTime / timeValues.length;
  if (average <= 0) return 0;

  return (
    (Math.sqrt(
      timeValues.reduce((sum, time) => {
        return sum + Math.pow(time - average, 2);
      }, 0) / timeValues.length,
    ) /
      average) *
    100
  );
};

export class AnalyticsService {
  constructor(private supabase: SupabaseClient<any, 'public', any>) {}

  async getStudentAdvancedAnalyticsDataset(): Promise<StudentAdvancedAnalyticsDataset> {
    const [studentsResult, classesResult, articlesResult, homeworkResult] =
      await Promise.all([
        this.supabase
          .from(TABLE_NAMES.STUDENT)
          .select('student_token, first_name, last_name, class_name'),
        this.supabase
          .from(TABLE_NAMES.CLASS)
          .select('class_token, class_name, semester_year'),
        this.supabase
          .from(TABLE_NAMES.LIBRARY)
          .select('id, title, grade, chapter, word_count')
          .eq('deleted', false),
        this.supabase
          .from(TABLE_NAMES.HOMEWORK)
          .select(
            'homework_token, title, created_at, article_token, class_token, deleted',
          ),
      ]);

    if (studentsResult.error) throw studentsResult.error;
    if (classesResult.error) throw classesResult.error;
    if (articlesResult.error) throw articlesResult.error;
    if (homeworkResult.error) throw homeworkResult.error;

    const dataset = {
      students: (studentsResult.data as StudentProfile[]) || [],
      classes: (classesResult.data as ClassProfile[]) || [],
      articles: (articlesResult.data as ArticleProfile[]) || [],
      homework: (homeworkResult.data as HomeworkV2Profile[]) || [],
      homeworkRelations: await this.getAllHomeworkRelations(),
    };

    await this.refreshAnalyticsCachesFromDataset(dataset);

    return dataset;
  }

  async getStudentsWithAnalytics(
    students: StudentProfile[],
    options: StudentAnalyticsOptions = {},
  ): Promise<StudentAnalyticsRow[]> {
    if (students.length === 0) return [];

    const studentTokens = students.map((student) => student.student_token);
    const { data, error } = await this.supabase
      .from(TABLE_NAMES.HOMEWORK_STUDENT_RELATION)
      .select('*')
      .in('student_token', studentTokens);

    if (error) {
      throw error;
    }

    const relationsByStudent = new Map<string, HomeworkStudentProfile[]>();
    ((data as HomeworkStudentProfile[]) || []).forEach((relation) => {
      if (!relationsByStudent.has(relation.student_token)) {
        relationsByStudent.set(relation.student_token, []);
      }
      relationsByStudent.get(relation.student_token)?.push(relation);
    });

    const analyticsRows = students.map((student) => {
      const relations = (
        relationsByStudent.get(student.student_token) || []
      ).filter(
        (relation) =>
          !options.scopeByStudentClass ||
          !student.class_token ||
          relation.class_token === student.class_token,
      );
      const uniqueRelations = Array.from(
        new Map(
          relations.map((relation) => [relation.homework_token, relation]),
        ).values(),
      );

      let completed = 0;
      let completedAttempts = 0;
      let totalTime = 0;
      let totalScore = 0;
      let lastActivity = '';

      uniqueRelations.forEach((relation) => {
        const activityAt =
          relation.completed_at || relation.updated_at || relation.created_at;
        if (activityAt && (!lastActivity || activityAt > lastActivity)) {
          lastActivity = activityAt;
        }

        if (!relation.completed) return;

        completed += 1;
        totalTime += Math.min(relation.total_time || 0, 1200000);

        const score = calculateAttemptScore(relation.question_attempted);
        if (score !== null) {
          totalScore += score;
          completedAttempts += 1;
        }
      });

      const totalHomework = uniqueRelations.length;
      const averageScore =
        completedAttempts > 0 ? totalScore / completedAttempts : 0;
      const averageTime = completed > 0 ? totalTime / completed : 0;
      const completionPercentage =
        totalHomework > 0 ? (completed / totalHomework) * 100 : 0;

      return {
        ...student,
        total_time: totalTime,
        average_time: averageTime,
        completed,
        total_students: totalHomework,
        percent_completed: completionPercentage,
        average_score: averageScore,
        analytics: {
          totalHomework,
          totalHomeworks: totalHomework,
          completedHomework: completed,
          averageScore,
          timeSpent: totalTime,
          lastActivity,
          completionPercentage,
        },
      };
    });

    await this.writeStudentAnalyticsCache(analyticsRows);

    return analyticsRows;
  }

  private async getAllHomeworkRelations() {
    const pageSize = 1000;
    let from = 0;
    let all: HomeworkStudentProfile[] = [];

    while (true) {
      const { data, error } = await this.supabase
        .from(TABLE_NAMES.HOMEWORK_STUDENT_RELATION)
        .select(
          'student_token, class_token, article_token, homework_token, completed, total_time',
        )
        .range(from, from + pageSize - 1);

      if (error) {
        throw error;
      }

      const batch = (data as HomeworkStudentProfile[]) || [];
      all = all.concat(batch);

      if (batch.length < pageSize) {
        break;
      }

      from += pageSize;
    }

    return all;
  }

  private async refreshAnalyticsCachesFromDataset(
    dataset: StudentAdvancedAnalyticsDataset,
  ) {
    const articleAnalytics = this.buildArticleAnalyticsFromDataset(dataset);

    console.log('[AnalyticsService] Refreshing analytics caches from dataset', {
      articlesWithRelations: articleAnalytics.length,
      students: dataset.students.length,
      classes: dataset.classes.length,
      relations: dataset.homeworkRelations.length,
    });

    if (articleAnalytics.length > 0) {
      await this.writeArticleAnalyticsCache(articleAnalytics);
    }

    if (dataset.students.length > 0) {
      await this.getStudentsWithAnalytics(dataset.students);
    }

    await this.writeClassAnalyticsCache(dataset);
  }

  private buildArticleAnalyticsFromDataset(
    dataset: StudentAdvancedAnalyticsDataset,
  ) {
    const articlesById = new Map(
      dataset.articles.map((article) => [article.id, article]),
    );
    const homeworkByToken = new Map(
      dataset.homework.map((homework) => [homework.homework_token, homework]),
    );
    const relationsByArticle = new Map<string, HomeworkStudentProfile[]>();

    dataset.homeworkRelations.forEach((relation) => {
      if (!relation.article_token) return;
      if (!relationsByArticle.has(relation.article_token)) {
        relationsByArticle.set(relation.article_token, []);
      }
      relationsByArticle.get(relation.article_token)?.push(relation);
    });

    return Array.from(relationsByArticle.entries())
      .map(([articleToken, relations]) => {
        const article = articlesById.get(articleToken);
        if (!article) return null;

        const homeworkTokens = Array.from(
          new Set(relations.map((relation) => relation.homework_token)),
        );
        const homeworkList = homeworkTokens
          .map((homeworkToken) => homeworkByToken.get(homeworkToken))
          .filter(Boolean);
        const completedRelations = relations.filter((relation) =>
          hasAttemptedAnswers(relation.question_attempted),
        );
        const timeValues: number[] = [];
        const sessionDurations: number[] = [];
        let totalTime = 0;
        let totalScore = 0;
        let completedCount = 0;

        completedRelations.forEach((relation) => {
          if (relation.total_time) {
            totalTime += relation.total_time;
            timeValues.push(relation.total_time);

            if (relation.created_at && relation.updated_at) {
              const sessionTime =
                new Date(relation.updated_at).getTime() -
                new Date(relation.created_at).getTime();
              if (sessionTime > 0) {
                sessionDurations.push(sessionTime);
              }
            }
          }

          const score = calculateAttemptScore(relation.question_attempted);
          if (score !== null) {
            totalScore += score;
          }
          completedCount++;
        });

        const avgSessionDuration =
          sessionDurations.length > 0
            ? sessionDurations.reduce((sum, duration) => sum + duration, 0) /
              sessionDurations.length
            : 0;

        return {
          ...article,
          total_time: totalTime,
          average_time: completedCount > 0 ? totalTime / completedCount : 0,
          completed: completedRelations.length,
          total_students: relations.length,
          percent_completed:
            relations.length > 0
              ? (completedRelations.length / relations.length) * 100
              : 0,
          average_score: completedCount > 0 ? totalScore / completedCount : 0,
          total_homeworks: homeworkList.length,
          homework_tokens: homeworkTokens,
          relation_count: relations.length,
          timeVariability: calculateTimeVariability(timeValues, totalTime),
          avgSessionDuration,
          timeValues,
          sessionDurations,
        } as ArticleAnalyticsRow;
      })
      .filter((row): row is ArticleAnalyticsRow => Boolean(row));
  }

  private async writeArticleAnalyticsCache(
    analyticsData: ArticleAnalyticsRow[],
  ) {
    await Promise.all(
      analyticsData.map(async (article) => {
        const error = await this.replaceCacheRow(
          TABLE_NAMES.HOMEWORK_ARTICLE_CACHE,
          'article_token',
          article.id,
          article,
        );

        if (error) {
          console.error(
            '[AnalyticsService] Failed to cache article analytics',
            {
              articleToken: article.id,
              error,
            },
          );
        }
      }),
    );

    const articleTokens = analyticsData.map((article) => article.id);
    const { data, error } = await this.supabase
      .from(TABLE_NAMES.HOMEWORK_ARTICLE_CACHE)
      .select('article_token')
      .in('article_token', articleTokens);

    if (error) {
      console.error(
        '[AnalyticsService] Failed to verify article cache writes',
        {
          articleTokens,
          error,
        },
      );
      return;
    }

    const cachedTokens = new Set((data || []).map((row) => row.article_token));
    const missingTokens = articleTokens.filter(
      (articleToken) => !cachedTokens.has(articleToken),
    );
    if (missingTokens.length > 0) {
      console.warn(
        '[AnalyticsService] Article cache rows missing after write',
        {
          missingTokens,
        },
      );
    }
  }

  private async writeStudentAnalyticsCache(
    analyticsRows: StudentAnalyticsRow[],
  ) {
    await Promise.all(
      analyticsRows.map(async (student) => {
        const error = await this.replaceCacheRow(
          TABLE_NAMES.HOMEWORK_STUDENT_CACHE,
          'student_token',
          student.student_token,
          student,
        );

        if (error) {
          console.error(
            '[AnalyticsService] Failed to cache student analytics',
            {
              studentToken: student.student_token,
              error,
            },
          );
        }
      }),
    );
  }

  private async writeClassAnalyticsCache(
    dataset: StudentAdvancedAnalyticsDataset,
  ) {
    const relationsByClass = new Map<string, HomeworkStudentProfile[]>();
    dataset.homeworkRelations.forEach((relation) => {
      if (!relationsByClass.has(relation.class_token)) {
        relationsByClass.set(relation.class_token, []);
      }
      relationsByClass.get(relation.class_token)?.push(relation);
    });

    await Promise.all(
      dataset.classes.map(async (classItem) => {
        const relations = relationsByClass.get(classItem.class_token) || [];
        const completed = relations.filter((relation) => relation.completed);
        const totalTime = completed.reduce(
          (sum, relation) => sum + (relation.total_time || 0),
          0,
        );
        const cacheRow = {
          ...classItem,
          completed: completed.length,
          total_students: relations.length,
          percent_completed:
            relations.length > 0
              ? (completed.length / relations.length) * 100
              : 0,
          average_time: completed.length > 0 ? totalTime / completed.length : 0,
          total_time: totalTime,
        };

        const error = await this.replaceCacheRow(
          TABLE_NAMES.HOMEWORK_CLASS_CACHE,
          'class_token',
          classItem.class_token,
          cacheRow,
        );

        if (error) {
          console.error('[AnalyticsService] Failed to cache class analytics', {
            classToken: classItem.class_token,
            error,
          });
        }
      }),
    );
  }

  private async replaceCacheRow(
    tableName: string,
    keyColumn: string,
    keyValue: string,
    data: any,
  ) {
    const cacheRow = {
      [keyColumn]: keyValue,
      data,
      created_at: new Date().toISOString(),
    };

    const updateResult = await this.supabase
      .from(tableName)
      .update(cacheRow)
      .eq(keyColumn, keyValue)
      .select(keyColumn);

    if (updateResult.error) {
      return updateResult.error;
    }

    if ((updateResult.data || []).length > 0) {
      return null;
    }

    const insertResult = await this.supabase.from(tableName).insert(cacheRow);
    return insertResult.error || null;
  }

  async getHomeworkAnalytics(
    homeworkToken: string,
    classToken: string,
  ): Promise<HomeworkAnalyticsResult> {
    const [studentsResult, homeworkResult, allHomeworkResult] =
      await Promise.all([
        this.supabase
          .from(TABLE_NAMES.HOMEWORK_STUDENT_RELATION)
          .select('*')
          .eq('homework_token', homeworkToken)
          .eq('class_token', classToken),
        this.supabase
          .from(TABLE_NAMES.HOMEWORK)
          .select('*')
          .eq('homework_token', homeworkToken)
          .maybeSingle(),
        this.supabase
          .from(TABLE_NAMES.HOMEWORK)
          .select('*')
          .eq('class_token', classToken),
      ]);

    if (studentsResult.error) {
      throw studentsResult.error;
    }

    if (homeworkResult.error) {
      console.warn('[AnalyticsService] Homework lookup failed', {
        homeworkToken,
        error: homeworkResult.error,
      });
    }

    if (allHomeworkResult.error) {
      console.warn('[AnalyticsService] Class homework lookup failed', {
        classToken,
        error: allHomeworkResult.error,
      });
    }

    const availableHomework = allHomeworkResult.data || [];
    const studentsData =
      (studentsResult.data as HomeworkStudentProfile[]) || [];

    if (studentsData.length === 0) {
      return { analytics: null, availableHomework };
    }

    const averageRawTime =
      studentsData.reduce(
        (sum, student) => sum + (student.total_time || 0),
        0,
      ) / studentsData.length;

    const students = studentsData.map((student) => {
      const questionAttempted = formatQuestionAttempted(
        student.question_attempted,
      );
      const totalQuestions = Object.keys(questionAttempted).length;
      const correctAnswers = Object.values(questionAttempted).reduce(
        (sum, value) => sum + value,
        0,
      );
      const completionPercentage =
        totalQuestions > 0 ? (correctAnswers / totalQuestions) * 100 : 0;

      let performance: EnhancedHomeworkStudentAnalytics['performance'] = 'poor';
      if (completionPercentage >= 90) performance = 'excellent';
      else if (completionPercentage >= 75) performance = 'good';
      else if (completionPercentage >= 60) performance = 'average';

      let timeCategory: EnhancedHomeworkStudentAnalytics['timeCategory'] =
        'average';
      if ((student.total_time || 0) < averageRawTime * 0.7) {
        timeCategory = 'fast';
      } else if ((student.total_time || 0) > averageRawTime * 1.3) {
        timeCategory = 'slow';
      }

      return {
        ...student,
        performance,
        timeCategory,
        completionPercentage,
      };
    });

    const completedStudents = students.filter((student) => student.completed);
    const completionRate = (completedStudents.length / students.length) * 100;
    const averageTime =
      students.reduce((sum, student) => sum + (student.total_time || 0), 0) /
      students.length;
    const averageScore =
      students.reduce(
        (sum, student) => sum + (student.completionPercentage || 0),
        0,
      ) / students.length;
    const difficultyDistribution = {
      easy: students.filter(
        (student) =>
          student.performance === 'excellent' || student.performance === 'good',
      ).length,
      medium: students.filter((student) => student.performance === 'average')
        .length,
      hard: students.filter((student) => student.performance === 'poor').length,
    };
    const timeDistribution = {
      fast: students.filter((student) => student.timeCategory === 'fast')
        .length,
      average: students.filter((student) => student.timeCategory === 'average')
        .length,
      slow: students.filter((student) => student.timeCategory === 'slow')
        .length,
    };
    const questionAnalytics = await this.getHomeworkQuestionAnalytics(
      homeworkResult.data,
      students,
    );

    return {
      analytics: {
        students,
        completionRate,
        averageTime,
        averageScore,
        difficultyDistribution,
        timeDistribution,
        questionAnalytics,
      },
      availableHomework,
    };
  }

  private async getHomeworkQuestionAnalytics(
    homeworkData: any,
    students: EnhancedHomeworkStudentAnalytics[],
  ) {
    const articleToken =
      homeworkData?.article_token || students.find(Boolean)?.article_token;
    if (!articleToken) return [];

    const { data: articleData, error } = await this.supabase
      .from(TABLE_NAMES.LIBRARY)
      .select('question')
      .eq('id', articleToken)
      .single();

    if (error || !articleData?.question) {
      return [];
    }

    const questions = parseQuestions(articleData.question);
    return Array.from({ length: questions.length }, (_, index) => {
      const questionIndex = index.toString();
      let correctAnswers = 0;
      let totalAttempts = 0;

      students.forEach((student) => {
        const attempted = formatQuestionAttempted(student.question_attempted);
        if (questionIndex in attempted) {
          totalAttempts++;
          if (attempted[questionIndex] === 1) {
            correctAnswers++;
          }
        }
      });

      const successRate =
        totalAttempts > 0 ? correctAnswers / totalAttempts : 0;
      let difficulty: HomeworkQuestionAnalytics['difficulty'] = 'medium';
      if (successRate >= 0.8) difficulty = 'easy';
      else if (successRate < 0.5) difficulty = 'hard';

      return {
        questionNumber: index + 1,
        correctAnswers,
        totalAttempts,
        difficulty,
      };
    });
  }
}

export const createAnalyticsService = (
  supabase: SupabaseClient<any, 'public', any>,
) => new AnalyticsService(supabase);
